import { computed, makeObservable } from "mobx";
import { observer, Provider } from "mobx-react";
import { Component, type ReactNode } from "react";
import { IntlProvider } from "react-intl";
import { Routes, Route } from "react-router";

import { LoadingSection } from "nvent-web/components/LoadingSection/LoadingSection";
import { messages } from "nvent-web/i18n";

import Api from "nvent-web/services/Api";
import PublicApi from "nvent-web/services/PublicApi";
import AppStore from "nvent-web/stores/App";
import { NotificationsStore } from "nvent-web/stores/Notifications";
import { AppAuth0Provider } from "nvent-web/services/auth0/AppAuth0Provider";
import { AppAuth0Connector } from "nvent-web/services/auth0/AppAuth0Connector";
import { PhotosStore } from "nvent-web/stores/Photos";
import { ProductsStore } from "nvent-web/stores/Products";
import { ProjectsStore } from "nvent-web/stores/Projects";
import { PromotionsStore } from "nvent-web/stores/Promotions";
import { RoomsStore } from "nvent-web/stores/Rooms";
import { TeamInstallersStore } from "nvent-web/stores/TeamInstallers";
import { UserStore } from "nvent-web/stores/User";
import { isFeatureEnabled } from "nvent-web/utils/features";
import { independentSelectionGuideRoutes } from "nvent-web/scenes/IndependentSelectionGuide/IndependentSelectionGuide.tsx";
import NotFound from "nvent-web/scenes/404";
import Redirect from "nvent-web/components/Redirect";
import Auth from "nvent-web/scenes/Auth";
import { authenticatedAppRoutes } from "./components/AuthenticatedApp";
import compose from "nvent-web/utils/compose";
import { withRouter, type Navigate } from "../utils/withRouter";
import ProjectCommissionReport from "../scenes/ProjectCommissionReport";
import { Role } from "../types/Role";
import { AsyncAdminApp } from "~/nvent-web-admin/AsyncAdminApp";

interface Injectables {
  api: Api;
  publicApi: PublicApi;
  app: AppStore;
  user: UserStore;
  projects: ProjectsStore;
  promotions: PromotionsStore;
  notifications: NotificationsStore;
  rooms: RoomsStore;
  products: ProductsStore;
  photos: PhotosStore;
  teamInstallers: TeamInstallersStore;
}

class App extends Component {
  private injectables: Injectables;

  get isLoading() {
    return this.injectables.user.isLoading;
  }

  constructor(props: { children: ReactNode; navigate: Navigate }) {
    super(props);

    makeObservable(this, {
      isLoading: computed,
    });

    const notifications = new NotificationsStore();

    const api = new Api(import.meta.env.VITE_API_URL || "", notifications);

    const publicApi = new PublicApi(
      import.meta.env.VITE_API_URL || "",
      notifications
    );

    const navigate = props.navigate;

    const user = new UserStore(api);
    const rooms = new RoomsStore(api, navigate);
    const app = new AppStore(user);

    this.injectables = {
      api,
      publicApi,
      user,
      app,
      projects: new ProjectsStore(user, api, navigate),
      promotions: new PromotionsStore(user, api),
      notifications,
      rooms,
      products: new ProductsStore(user, rooms, api),
      photos: new PhotosStore(rooms, api),
      teamInstallers: new TeamInstallersStore(api),
    };
  }

  render() {
    const { app } = this.injectables;

    const currentLocale = app.locale;

    return (
      <Provider {...this.injectables}>
        <IntlProvider locale={currentLocale} messages={messages[currentLocale]}>
          <AppAuth0Provider>
            <AppAuth0Connector />
            {this.renderContent()}
          </AppAuth0Provider>
        </IntlProvider>
      </Provider>
    );
  }

  private renderContent(): ReactNode {
    if (this.isLoading) {
      return <LoadingSection />;
    }

    const { app, user, notifications } = this.injectables;

    const isUserAdmin = [Role.admin, Role.superAdmin].includes(
      this.injectables.user.role
    );

    return (
      <Routes>
        <Route path="/auth/*" Component={Auth} />
        {user.isReady ? (
          <>
            {isUserAdmin ? (
              <Route
                path="admin/*"
                element={
                  <AsyncAdminApp isSuperAdmin={user.role === Role.superAdmin} />
                }
              />
            ) : (
              <Route path="admin/*" element={<Redirect to="/" />} />
            )}
            {authenticatedAppRoutes({
              app,
              notifications,
              user,
            })}
          </>
        ) : (
          <>
            <Route path="/">
              <Route
                path="projects/:projectId/commission-report"
                Component={ProjectCommissionReport}
              />
              {independentSelectionGuideRoutes({
                createProjectDisabled: !isFeatureEnabled(
                  "selection-guide-create-project"
                ),
              })}
            </Route>
            <Route index element={<Redirect to="/auth/login" />} />
          </>
        )}
        <Route
          path="*"
          element={user.isReady ? <NotFound /> : <Redirect to="/auth/login" />}
        />
      </Routes>
    );
  }
}

const AppComponent = compose(withRouter, observer)(App);

AppComponent.displayName = "App";

export default AppComponent;
